import { ref, computed, type Ref, type ComputedRef } from 'vue'

export interface UseSelectionReturn<T, K extends string | number> {
  selectedKeys: Ref<Set<K>>
  isAllSelected: ComputedRef<boolean>
  hasSelection: ComputedRef<boolean>
  selectedItems: ComputedRef<T[]>
  toggleSelect: (key: K) => void
  toggleSelectAll: () => void
  clearSelection: () => void
  removeKeys: (keys: K[]) => void
}

/**
 * Composable for managing item selection state in lists/tables.
 * Handles single select, select all, and bulk selection operations.
 *
 * @param items - Reactive reference to the array of items
 * @param getKey - Function to extract unique key from each item
 */
export function useSelection<T, K extends string | number>(
  items: Ref<T[]>,
  getKey: (item: T) => K
): UseSelectionReturn<T, K> {
  const selectedKeys = ref<Set<K>>(new Set()) as Ref<Set<K>>

  const isAllSelected = computed(
    () => items.value.length > 0 && selectedKeys.value.size === items.value.length
  )

  const hasSelection = computed(() => selectedKeys.value.size > 0)

  const selectedItems = computed(() =>
    items.value.filter((item) => selectedKeys.value.has(getKey(item)))
  )

  const toggleSelect = (key: K) => {
    const newSet = new Set(selectedKeys.value)
    if (newSet.has(key)) {
      newSet.delete(key)
    } else {
      newSet.add(key)
    }
    selectedKeys.value = newSet
  }

  const toggleSelectAll = () => {
    if (isAllSelected.value) {
      selectedKeys.value = new Set()
    } else {
      selectedKeys.value = new Set(items.value.map((item) => getKey(item)))
    }
  }

  const clearSelection = () => {
    selectedKeys.value = new Set()
  }

  const removeKeys = (keys: K[]) => {
    const newSet = new Set(selectedKeys.value)
    keys.forEach((key) => newSet.delete(key))
    selectedKeys.value = newSet
  }

  return {
    selectedKeys,
    isAllSelected,
    hasSelection,
    selectedItems,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    removeKeys
  }
}
